import {
  collection,
  onSnapshot,
  query,
  where,
  addDoc,
  doc,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { db } from "../firebase";
import { useAuth } from "./AuthContext";
import { useSearchParams } from "react-router-dom";

const DataContext = createContext();

export const useData = () => {
  const ctx = useContext(DataContext);
  if (!ctx) throw new Error("useData must be used within DataProvider");
  return ctx;
};

export const DataProvider = ({ children }) => {
  const { clientId, isAdmin } = useAuth();
  const [searchParams] = useSearchParams();
  const ctid = searchParams.get("ctid");

  const effectiveClientId = useMemo(() => {
    if (isAdmin && ctid) return ctid;
    if (!isAdmin && clientId) return clientId;
    return null;
  }, [isAdmin, ctid, clientId]);

  const [clients, setClients] = useState([]);
  const [sows, setSows] = useState([]);
  const [requirements, setRequirements] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [ideas, setIdeas] = useState([]);
  const [notes, setNotes] = useState([]);
  const [developers, setDevelopers] = useState([]);
  const [users, setUsers] = useState([]);

  const [selectedClientId, setSelectedClientId] = useState("");
  const [selectedSowId, setSelectedSowId] = useState(null);

  useEffect(() => {
    if (isAdmin && ctid) {
      setSelectedClientId(ctid);
    } else if (!isAdmin && clientId) {
      setSelectedClientId(clientId);
    }
  }, [isAdmin, ctid, clientId]);

  useEffect(() => {
    const clientSows = sows.filter((s) => s.clientId === selectedClientId);
    const activeSow = clientSows.find((s) => s.status === "active");
    const stillValid = clientSows.some((s) => s.id === selectedSowId);

    if (!stillValid && activeSow) {
      setSelectedSowId(activeSow.id);
    }
  }, [selectedClientId, sows]);

  const selectedClient = useMemo(() => {
    return clients.find((c) => c.id === effectiveClientId) || null;
  }, [clients, effectiveClientId]);

  const selectedSow = useMemo(() => {
    return sows.find((s) => s.id === selectedSowId) || null;
  }, [sows, selectedSowId]);

  const subscribe = (colName, setState, extraFilters = []) => {
    if (!effectiveClientId && !isAdmin) {
      console.warn(`⚠️ Skipping subscription to ${colName}: No clientId`);
      return () => {};
    }

    const colRef = collection(db, colName);
    const q =
      effectiveClientId && !isAdmin
        ? query(
            colRef,
            where("clientId", "==", effectiveClientId),
            ...extraFilters
          )
        : isAdmin && ctid
        ? query(colRef, where("clientId", "==", ctid), ...extraFilters)
        : colRef;

    const unsub = onSnapshot(q, (snapshot) => {
      const docs = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      console.log(`📥 ${colName}:`, docs);
      setState(docs);
    });

    return unsub;
  };

  useEffect(() => {
    if (!effectiveClientId && !isAdmin) return;

    const unsubClients = subscribe("clients", setClients);
    const unsubSows = subscribe("sows", setSows);
    const unsubRequirements = subscribe("requirements", setRequirements);
    const unsubTasks = subscribe("tasks", setTasks);
    const unsubIdeas = subscribe("ideas", setIdeas);
    const unsubNotes = subscribe("notes", setNotes);

    const unsubDevelopers = onSnapshot(
      query(collection(db, "users"), where("isDev", "==", true)),
      (snapshot) => {
        const docs = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        console.log("📥 developers:", docs);
        setDevelopers(docs);
      }
    );

    const unsubUsers = onSnapshot(collection(db, "users"), (snapshot) => {
      const docs = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      console.log("📥 users:", docs);
      setUsers(docs);
    });

    return () => {
      unsubClients();
      unsubSows();
      unsubRequirements();
      unsubTasks();
      unsubIdeas();
      unsubNotes();
      unsubDevelopers();
      unsubUsers();
    };
  }, [effectiveClientId, isAdmin]);

  const enrichedSow = useMemo(() => {
    if (!selectedSow) return null;

    const enrichedDeliverables = (selectedSow.deliverables || []).map((del) => {
      const relatedRequirements = requirements
        .filter((req) => req.deliverableId === del.id)
        .map((req) => ({
          ...req,
          deliverableName: del.name,
          deliverableId: del.id,
          tasks: tasks.filter((t) => t.requirementId === req.id),
          notes: notes.filter((n) => n.parentId === req.id),
        }));

      return {
        ...del,
        requirements: relatedRequirements,
      };
    });

    return {
      ...selectedSow,
      deliverables: enrichedDeliverables,
    };
  }, [selectedSow, requirements, tasks, notes]);

  const enrichedTasks = useMemo(() => {
    return (
      enrichedSow?.deliverables?.flatMap((del) =>
        del.requirements.flatMap((req) =>
          (req.tasks || []).map((task) => ({
            ...task,
            assignedTo: task.assignedTo || "",
            requirementName: req.title,
            deliverableName: req.deliverableName,
            deliverableId: req.deliverableId,
            requirementId: req.id,
            assignedToReq: req.assignedTo,
            detail: req.detail,
            taskDesc: task.detail,
            status: req.status,
            linked_email: req.linked_email,
            dueDate: req.dueDate,
          }))
        )
      ) || []
    );
  }, [enrichedSow]);

  const addDocument = async (collectionName, data) => {
    const docRef = await addDoc(collection(db, collectionName), data);
    return docRef.id;
  };

  const updateDocument = async (collectionName, docId, updatedData) => {
    const docRef = doc(db, collectionName, docId);
    await updateDoc(docRef, updatedData);
  };

  const deleteDocument = async (collectionName, docId) => {
    const docRef = doc(db, collectionName, docId);
    await deleteDoc(docRef);
  };

  const clientUsers = useMemo(() => {
    if (!effectiveClientId) return developers;

    const filteredUsers = users.filter((u) => u.clientId === effectiveClientId);
    const combined = [...filteredUsers, ...developers];

    // Deduplicate by user.id
    const uniqueById = new Map();
    combined.forEach((user) => {
      if (!uniqueById.has(user.id)) {
        uniqueById.set(user.id, user);
      }
    });

    let uniqueUsers = Array.from(uniqueById.values());

    // Move Ryan to the top
    uniqueUsers.sort((a, b) => {
      if (a.email === "ryan.bentham@inovora.com") return -1;
      if (b.email === "ryan.bentham@inovora.com") return 1;
      return 0;
    });

    return uniqueUsers;
  }, [users, developers, effectiveClientId]);

  return (
    <DataContext.Provider
      value={{
        clients,
        sows,
        requirements,
        tasks,
        ideas,
        notes,
        developers,
        users,
        selectedClientId,
        setSelectedClientId,
        selectedClient,
        selectedSowId,
        setSelectedSowId,
        selectedSow,
        enrichedSow,
        enrichedTasks,
        addDocument,
        updateDocument,
        deleteDocument,
        clientUsers,
      }}
    >
      {children}
    </DataContext.Provider>
  );
};
